import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Package, Home, Car, Camera, Wrench } from "lucide-react";
import type { BundleWithRelations } from "@/contracts/bundles";

interface BundleCardProps {
  bundle: BundleWithRelations;
}

export function BundleCard({ bundle }: BundleCardProps) {
  const priceRange = bundle.total_price_min && bundle.total_price_max
    ? `$${bundle.total_price_min}–$${bundle.total_price_max}`
    : 'Price varies';

  // Map kit type to icon
  const getKitIcon = () => {
    const iconClass = "h-16 w-16 text-primary";
    switch (bundle.kit_type.slug) {
      case 'open-house':
        return <Home className={iconClass} />;
      case 'car-kit':
        return <Car className={iconClass} />;
      case 'photography':
        return <Camera className={iconClass} />;
      case 'staging':
        return <Wrench className={iconClass} />;
      default:
        return <Package className={iconClass} />;
    }
  };

  return (
    <Link to={`/gearbundles/${bundle.slug}`}>
      <Card className="group hover:shadow-lg transition-all duration-300 hover:border-primary/50 h-full">
        <CardContent className="p-4">
          {/* Kit Type Visual */}
          <div className="w-full h-32 bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg mb-3 flex items-center justify-center border border-primary/20">
            {getKitIcon()}
          </div>

          {/* Kit Type & Version */}
          <div className="space-y-2 mb-3">
            <h3 className="font-semibold text-sm line-clamp-2 group-hover:text-primary transition-colors">
              {bundle.kit_type.name}
            </h3>
            <div className="flex items-center gap-2">
              <Badge 
                variant="secondary" 
                className="text-xs"
                style={{ 
                  backgroundColor: bundle.version.badge_color || undefined 
                }}
              >
                {bundle.version.name}
              </Badge>
              <span className="text-xs text-muted-foreground">via {bundle.retailer.name}</span>
            </div>
          </div>

          {/* Summary */}
          <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
            {bundle.summary}
          </p>

          {/* Price & Items */}
          <div className="flex items-center justify-between pt-3 border-t">
            <div>
              <p className="text-sm font-semibold text-foreground">{priceRange}</p>
              <p className="text-xs text-muted-foreground">{bundle.item_count} items</p>
            </div>
            <div className="text-xs text-muted-foreground">
              {bundle.views_count || 0} views
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
